import React, { useMemo, useState } from 'react'
import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption as HeadlessComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'

// Generic, value-controlled Headless UI typeahead. Promoted from
// iam/app/javascript/views/Onboard/IndustryCombobox.tsx: same grouping,
// sticky-header, and filter-memo shape, but parameterized over
// ComboboxOption[] with no react-hook-form Controller and no consts
// coupling, so the library primitive stays form-lib agnostic. Callers that
// need react-hook-form wire it up with their own Controller (iam keeps
// that wrapper at the app layer).

export interface ComboboxOption {
  value: string
  label: string
  group?: string
  hint?: string
}

export interface ComboboxProps {
  options: ComboboxOption[]
  value: string | null
  onChange: (value: string | null) => void
  onBlur?: () => void
  placeholder?: string
  disabled?: boolean
  error?: boolean
  id?: string
  filterKeys?: ('label' | 'group' | 'value')[]
  'aria-label'?: string
}

const DEFAULT_FILTER_KEYS: ('label' | 'group' | 'value')[] = ['label', 'group', 'value']

const Combobox = ({
  options,
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  error,
  id,
  filterKeys = DEFAULT_FILTER_KEYS,
  'aria-label': ariaLabel,
}: ComboboxProps) => {
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return options
    return options.filter(o => filterKeys.some(key => (o[key] ?? '').toLowerCase().includes(q)))
  }, [options, query, filterKeys])

  // Group filtered options by `group`, preserving first-seen group order
  // (not alphabetical); ungrouped options fall under a single `undefined`
  // bucket rendered without a sticky header.
  const grouped = useMemo(() => {
    const byGroup = new Map<string, ComboboxOption[]>()
    filtered.forEach(o => {
      const key = o.group ?? ''
      if (!byGroup.has(key)) byGroup.set(key, [])
      byGroup.get(key)!.push(o)
    })
    return Array.from(byGroup, ([key, items]) => ({ group: key || undefined, items }))
  }, [filtered])

  const selected = options.find(o => o.value === value) ?? null

  return (
    <HeadlessCombobox
      value={selected}
      onChange={(opt: ComboboxOption | null) => onChange(opt?.value ?? null)}
      disabled={disabled}
    >
      <div className="relative">
        <ComboboxInput
          id={id}
          aria-label={ariaLabel}
          className={`block w-full rounded-md border bg-surface py-2 pl-3 pr-10 text-sm
            text-ink placeholder:text-ink-muted focus:outline-hidden focus:ring-2
            focus:ring-interactive focus:border-interactive disabled:bg-surface-muted
            disabled:text-ink-muted ${error ? 'border-status-danger' : 'border-border-token'}`}
          onChange={e => setQuery(e.target.value)}
          onBlur={onBlur}
          displayValue={(opt: ComboboxOption | null) => opt?.label ?? ''}
          placeholder={placeholder}
          autoComplete="off"
        />
        <ComboboxButton
          aria-label="Open options"
          className="absolute inset-y-0 right-0 flex items-center px-2 text-ink-muted
            hover:text-ink focus:outline-hidden"
        >
          <ChevronUpDownIcon className="h-4 w-4" aria-hidden="true" />
        </ComboboxButton>

        <ComboboxOptions
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md
            border border-border-token bg-surface py-1 text-sm shadow-lg
            focus:outline-hidden"
        >
          {grouped.length === 0 && (
            <div className="px-3 py-2 text-ink-muted">No matches</div>
          )}

          {grouped.map(({ group, items }) => (
            <div key={group ?? '__ungrouped__'}>
              {group && (
                <div className="sticky top-0 z-10 bg-surface-muted px-3 py-1 text-xs font-semibold
                  uppercase tracking-wide text-ink-muted">
                  {group}
                </div>
              )}
              {items.map(opt => (
                <HeadlessComboboxOption
                  key={opt.value}
                  value={opt}
                  className={({ focus, selected: isSelected }) => `
                    cursor-pointer px-3 py-2 flex items-center justify-between
                    ${focus ? 'bg-surface-muted text-ink' : 'text-ink'}
                    ${isSelected ? 'font-semibold' : 'font-normal'}
                  `}
                >
                  <span>{opt.label}</span>
                  {opt.hint && <span className="text-xs text-ink-muted">{opt.hint}</span>}
                  {!opt.hint && (
                    <span className="text-xs text-ink-muted tabular-nums">{opt.value}</span>
                  )}
                </HeadlessComboboxOption>
              ))}
            </div>
          ))}
        </ComboboxOptions>
      </div>
    </HeadlessCombobox>
  )
}

export default Combobox
